/**
 * Navigation Visibility Panel (WQ-146)
 *
 * Super-admin-only toggle tree for controlling which navigation groups
 * and items are visible per tenant. Uses existing navigation_overrides
 * JSONB field with hiddenGroups/hiddenItems arrays.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { Tenant } from '../../types';

type AuthFetch = (url: string, options?: RequestInit) => Promise<Response>;

interface NavItem {
  id: string;
  label: string;
  icon?: string;
  viewKey?: string;
  requiredRoles?: string[];
}

interface NavGroup {
  id: string;
  label: string;
  icon?: string;
  items: NavItem[];
}

interface NavigationVisibilityPanelProps {
  tenant: Tenant;
  authFetch: AuthFetch;
  onSaved?: () => void;
}

export const NavigationVisibilityPanel: React.FC<NavigationVisibilityPanelProps> = ({
  tenant,
  authFetch,
  onSaved,
}) => {
  const [templateGroups, setTemplateGroups] = useState<NavGroup[]>([]);
  const [templateSlug, setTemplateSlug] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());

  // Pending overrides (what will be saved)
  const currentOverrides = (tenant.navigation_overrides || {}) as Record<string, unknown>;
  const initialHiddenGroups = (currentOverrides.hiddenGroups as string[]) || [];
  const initialHiddenItems = (currentOverrides.hiddenItems as string[]) || [];

  const [hiddenGroups, setHiddenGroups] = useState<Set<string>>(new Set(initialHiddenGroups));
  const [hiddenItems, setHiddenItems] = useState<Set<string>>(new Set(initialHiddenItems));

  // Check if changes are pending
  const hasChanges =
    JSON.stringify([...hiddenGroups].sort()) !== JSON.stringify([...initialHiddenGroups].sort()) ||
    JSON.stringify([...hiddenItems].sort()) !== JSON.stringify([...initialHiddenItems].sort());

  // Fetch template navigation tree
  useEffect(() => {
    const fetchNav = async () => {
      const tenantExt = tenant as Tenant & { template_id?: string };
      if (!tenantExt.template_id) {
        setLoading(false);
        return;
      }

      try {
        // Fetch effective schema to get template slug
        const schemaRes = await authFetch(`/api/admin/tenants/${tenant.id}/effective-schema`);
        if (!schemaRes.ok) throw new Error('Failed to fetch effective schema');
        const schema = await schemaRes.json();
        setTemplateSlug(schema.template_slug || '');

        // Fetch templates list to get raw navigation
        const templatesRes = await authFetch('/api/admin/templates');
        if (!templatesRes.ok) throw new Error('Failed to fetch templates');
        const templatesData = await templatesRes.json();
        const templates = templatesData.templates || templatesData;
        const tmpl = Array.isArray(templates)
          ? templates.find((t: { slug: string }) => t.slug === schema.template_slug)
          : null;

        if (tmpl?.navigation?.groups) {
          setTemplateGroups(tmpl.navigation.groups);
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load navigation');
      } finally {
        setLoading(false);
      }
    };

    fetchNav();
  }, [tenant, authFetch]);

  const toggleGroup = useCallback((groupId: string) => {
    setHiddenGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const toggleItem = useCallback((itemId: string) => {
    setHiddenItems(prev => {
      const next = new Set(prev);
      if (next.has(itemId)) {
        next.delete(itemId);
      } else {
        next.add(itemId);
      }
      return next;
    });
  }, []);

  const toggleExpand = useCallback((groupId: string) => {
    setExpandedGroups(prev => {
      const next = new Set(prev);
      if (next.has(groupId)) {
        next.delete(groupId);
      } else {
        next.add(groupId);
      }
      return next;
    });
  }, []);

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const newOverrides = {
        ...currentOverrides,
        hiddenGroups: [...hiddenGroups],
        hiddenItems: [...hiddenItems],
      };

      const res = await authFetch(`/api/admin/tenants/${tenant.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ navigation_overrides: newOverrides }),
      });

      if (!res.ok) throw new Error('Failed to save navigation overrides');
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setHiddenGroups(new Set());
    setHiddenItems(new Set());
  };

  // No template assigned
  const tenantExt = tenant as Tenant & { template_id?: string };
  if (!loading && !tenantExt.template_id) {
    return (
      <div style={{ textAlign: 'center', padding: '32px 16px', color: 'var(--text-secondary)' }}>
        <div style={{ fontSize: '32px', marginBottom: '12px', opacity: 0.5 }}>&#9776;</div>
        <div style={{ fontSize: '14px', fontWeight: 500 }}>No schema template assigned</div>
        <div style={{ fontSize: '13px', marginTop: '8px', opacity: 0.7 }}>
          Navigation visibility is only available for tenants with a template.
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div style={{ textAlign: 'center', padding: '32px', color: 'var(--text-secondary)' }}>
        Loading navigation tree...
      </div>
    );
  }

  const hiddenGroupCount = hiddenGroups.size;
  const hiddenItemCount = hiddenItems.size;
  const totalGroups = templateGroups.length;
  const totalItems = templateGroups.reduce((sum, g) => sum + g.items.length, 0);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <div style={{ fontSize: '15px', fontWeight: 600, color: 'var(--text-primary)' }}>
            Navigation Visibility
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
            Toggle groups and items on/off. Hidden items won't appear in the tenant's navigation.
          </div>
        </div>
        {templateSlug && (
          <div style={{
            fontSize: '11px', padding: '4px 10px',
            background: 'var(--bg-tertiary, rgba(255,255,255,0.05))',
            borderRadius: '6px', color: 'var(--text-secondary)',
          }}>
            Template: {templateSlug}
          </div>
        )}
      </div>

      {/* Stats bar */}
      <div style={{
        display: 'flex', gap: '16px', fontSize: '12px', color: 'var(--text-secondary)',
        padding: '8px 12px', background: 'var(--bg-tertiary, rgba(255,255,255,0.03))', borderRadius: '8px',
      }}>
        <span>{totalGroups} groups</span>
        <span>{totalItems} items</span>
        {(hiddenGroupCount > 0 || hiddenItemCount > 0) && (
          <span style={{ color: 'var(--warning, #f59e0b)' }}>
            {hiddenGroupCount > 0 && `${hiddenGroupCount} group${hiddenGroupCount > 1 ? 's' : ''} hidden`}
            {hiddenGroupCount > 0 && hiddenItemCount > 0 && ', '}
            {hiddenItemCount > 0 && `${hiddenItemCount} item${hiddenItemCount > 1 ? 's' : ''} hidden`}
          </span>
        )}
      </div>

      {error && (
        <div style={{
          padding: '8px 12px', borderRadius: '6px', fontSize: '13px',
          background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid rgba(239, 68, 68, 0.2)',
        }}>
          {error}
        </div>
      )}

      {/* Toggle tree */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: '6px', maxHeight: '400px', overflowY: 'auto' }}>
        {templateGroups.map(group => {
          const isGroupHidden = hiddenGroups.has(group.id);
          const isExpanded = expandedGroups.has(group.id);
          const visibleItemCount = group.items.filter(item => !hiddenItems.has(item.id)).length;

          return (
            <div
              key={group.id}
              style={{
                border: '1px solid var(--glass-border, rgba(255,255,255,0.08))',
                borderRadius: '10px',
                overflow: 'hidden',
                opacity: isGroupHidden ? 0.5 : 1,
                transition: 'opacity 0.2s ease',
              }}
            >
              {/* Group header */}
              <div
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px',
                  padding: '10px 12px',
                  background: 'var(--bg-tertiary, rgba(255,255,255,0.03))',
                  cursor: 'pointer',
                }}
                onClick={() => !isGroupHidden && toggleExpand(group.id)}
              >
                <span style={{ fontSize: '10px', color: 'var(--text-secondary)', width: '12px', textAlign: 'center' }}>
                  {isGroupHidden ? '' : isExpanded ? '\u25BC' : '\u25B6'}
                </span>
                <span style={{ flex: 1, fontSize: '13px', fontWeight: 600, color: 'var(--text-primary)' }}>
                  {group.label}
                </span>
                {isGroupHidden && (
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    (all {group.items.length} items hidden)
                  </span>
                )}
                {!isGroupHidden && (
                  <span style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>
                    {visibleItemCount}/{group.items.length}
                  </span>
                )}
                {/* Group toggle */}
                <button
                  onClick={(e) => { e.stopPropagation(); toggleGroup(group.id); }}
                  style={{
                    width: '36px', height: '20px', borderRadius: '10px', border: 'none',
                    background: isGroupHidden
                      ? 'var(--bg-secondary, rgba(255,255,255,0.1))'
                      : 'var(--success, #22c55e)',
                    position: 'relative', cursor: 'pointer', transition: 'background 0.2s ease',
                    flexShrink: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute', top: '2px',
                    left: isGroupHidden ? '2px' : '18px',
                    width: '16px', height: '16px', borderRadius: '50%',
                    background: '#fff', transition: 'left 0.2s ease',
                  }} />
                </button>
              </div>

              {/* Items list */}
              {isExpanded && !isGroupHidden && (
                <div style={{ padding: '4px 0' }}>
                  {group.items.map(item => {
                    const isItemHidden = hiddenItems.has(item.id);
                    return (
                      <div
                        key={item.id}
                        style={{
                          display: 'flex', alignItems: 'center', gap: '8px',
                          padding: '6px 12px 6px 32px',
                          opacity: isItemHidden ? 0.5 : 1,
                          transition: 'opacity 0.15s ease',
                        }}
                      >
                        <span style={{ flex: 1, fontSize: '12px', color: 'var(--text-primary)' }}>
                          {item.label}
                        </span>
                        {isItemHidden && (
                          <span style={{ fontSize: '10px', color: 'var(--text-secondary)' }}>(hidden)</span>
                        )}
                        <button
                          onClick={() => toggleItem(item.id)}
                          style={{
                            width: '32px', height: '18px', borderRadius: '9px', border: 'none',
                            background: isItemHidden
                              ? 'var(--bg-secondary, rgba(255,255,255,0.1))'
                              : 'var(--success, #22c55e)',
                            position: 'relative', cursor: 'pointer', transition: 'background 0.2s ease',
                            flexShrink: 0,
                          }}
                        >
                          <span style={{
                            position: 'absolute', top: '2px',
                            left: isItemHidden ? '2px' : '16px',
                            width: '14px', height: '14px', borderRadius: '50%',
                            background: '#fff', transition: 'left 0.2s ease',
                          }} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Action buttons */}
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '8px' }}>
        <button
          className="btn btn-secondary"
          onClick={handleReset}
          disabled={hiddenGroups.size === 0 && hiddenItems.size === 0}
          style={{ fontSize: '13px' }}
        >
          Reset All
        </button>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={!hasChanges || saving}
          style={{ fontSize: '13px', minWidth: '120px' }}
        >
          {saving ? 'Saving...' : 'Save Changes'}
        </button>
      </div>
    </div>
  );
};

export default NavigationVisibilityPanel;
